<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import powerDataAPI from 'dashboard/api/powerData';
import { fraseDaRecusa } from 'dashboard/helper/recusaDoAcesso';

import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';

const props = defineProps({
  storeId: { type: [String, Number], required: true },
  powerData: { type: Object, default: () => ({}) },
});
const emit = defineEmits(['updated']);

const { t } = useI18n();
const store = useStore();
const router = useRouter();
const accountId = store.getters.getCurrentAccountId;

const name = ref('');
const language = ref('portuguese');
const autoTag = ref(false);
const isLoading = ref(false);
const copied = ref(false);
const deleteDialogRef = ref(null);

watch(
  () => props.powerData,
  val => {
    name.value = val?.name || '';
    language.value = val?.language || 'portuguese';
    autoTag.value = Boolean(val?.auto_tag);
  },
  { immediate: true, deep: true }
);

// 'simple' é o dicionário do Postgres que não aplica stemming de idioma nenhum:
// é a escolha honesta para base multilíngue, onde acertar um idioma erraria o outro.
const languageOptions = computed(() => [
  { value: 'portuguese', label: t('POWER_DATA.LANGUAGES.PORTUGUESE') },
  { value: 'english', label: t('POWER_DATA.LANGUAGES.ENGLISH') },
  { value: 'spanish', label: t('POWER_DATA.LANGUAGES.SPANISH') },
  { value: 'simple', label: t('POWER_DATA.LANGUAGES.UNSET') },
]);

const encryptedId = computed(() => props.powerData?.encrypted_id || '');

const hasChanges = computed(
  () =>
    name.value.trim() !== (props.powerData?.name || '') ||
    language.value !== (props.powerData?.language || 'portuguese') ||
    autoTag.value !== Boolean(props.powerData?.auto_tag)
);

async function save() {
  if (!name.value.trim()) return;
  isLoading.value = true;
  try {
    await powerDataAPI.update(accountId, props.storeId, {
      name: name.value.trim(),
      language: language.value,
      auto_tag: autoTag.value,
    });
    useAlert(t('POWER_DATA.SETTINGS.SAVED'));
    emit('updated');
  } catch (e) {
    useAlert(fraseDaRecusa(e) || t('POWER_DATA.SETTINGS.SAVE_ERROR'));
  } finally {
    isLoading.value = false;
  }
}

async function deleteStore() {
  try {
    await powerDataAPI.destroy(accountId, props.storeId);
    router.push({ name: 'power_data_index' });
  } catch (e) {
    useAlert(fraseDaRecusa(e) || t('POWER_DATA.DELETE_ERROR'));
  }
}

function copyId() {
  navigator.clipboard.writeText(encryptedId.value);
  copied.value = true;
  setTimeout(() => {
    copied.value = false;
  }, 2000);
}
</script>

<template>
  <div class="flex flex-col max-w-xl gap-8">
    <div class="flex flex-col gap-4">
      <Input
        v-model="name"
        :label="t('POWER_DATA.SETTINGS.NAME')"
        :placeholder="t('POWER_DATA.NAME_PLACEHOLDER')"
      />

      <div class="flex flex-col gap-1">
        <label class="text-body-main text-n-slate-12">
          {{ t('POWER_DATA.SETTINGS.LANGUAGE') }}
        </label>
        <Select v-model="language" :options="languageOptions" />
        <p class="text-body-small text-n-slate-11">
          {{ t('POWER_DATA.SETTINGS.LANGUAGE_HINT') }}
        </p>
      </div>

      <div
        class="flex items-start justify-between gap-4 p-3 border rounded-lg border-n-weak"
      >
        <div class="flex flex-col gap-1">
          <span class="text-body-main text-n-slate-12">
            {{ t('POWER_DATA.SETTINGS.AUTO_TAG') }}
          </span>
          <p class="text-body-small text-n-slate-11">
            {{
              autoTag
                ? t('POWER_DATA.SETTINGS.AUTO_TAG_ON')
                : t('POWER_DATA.SETTINGS.AUTO_TAG_OFF')
            }}
          </p>
        </div>
        <Switch v-model="autoTag" class="flex-shrink-0 mt-1" />
      </div>

      <div>
        <Button
          :label="t('POWER_DATA.SAVE')"
          size="sm"
          :disabled="!hasChanges || !name.trim()"
          :is-loading="isLoading"
          @click="save"
        />
      </div>
    </div>

    <!-- O identificador da base é para chamada de API: ferramenta de plataforma. Sem guarda
         própria: a aba inteira só existe para o super admin (Show.vue, POWERDATA-ACESSO-01). -->
    <div class="flex flex-col gap-2 pt-6 border-t border-n-weak">
      <label class="text-body-main text-n-slate-12">
        {{ t('POWER_DATA.SETTINGS.IDENTIFIER') }}
      </label>
      <div class="flex items-center gap-2">
        <code
          class="flex-1 px-3 py-2 font-mono truncate rounded-md text-body-small bg-n-alpha-2 text-n-slate-12"
        >
          {{ encryptedId }}
        </code>
        <Button
          v-tooltip.top="t('POWER_DATA.SETTINGS.COPY')"
          :icon="copied ? 'i-lucide-check' : 'i-lucide-copy'"
          slate
          sm
          @click="copyId"
        />
      </div>
      <p class="text-body-small text-n-slate-11">
        {{ t('POWER_DATA.SETTINGS.IDENTIFIER_HINT') }}
      </p>
    </div>

    <div
      class="flex items-center justify-between gap-4 p-4 border rounded-lg border-n-ruby-5 bg-n-ruby-2"
    >
      <div class="flex flex-col gap-1">
        <h4 class="text-body-main font-medium text-n-ruby-11">
          {{ t('POWER_DATA.DELETE_TITLE') }}
        </h4>
        <p class="text-body-small text-n-slate-11">
          {{ t('POWER_DATA.SETTINGS.DELETE_HINT') }}
        </p>
      </div>
      <Button
        :label="t('POWER_DATA.DELETE_BUTTON')"
        ruby
        size="sm"
        class="flex-shrink-0"
        @click="deleteDialogRef?.open()"
      />
    </div>

    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('POWER_DATA.DELETE_TITLE')"
      :description="
        t('POWER_DATA.DELETE_WARNING', { name: powerData?.name || '' })
      "
      :confirm-button-label="t('POWER_DATA.DELETE_YES')"
      :cancel-button-label="t('POWER_DATA.CANCEL')"
      @confirm="deleteStore"
    />
  </div>
</template>
